import { AxiosPromise } from 'axios';
import { Logger } from 'fsts';
import { SearchParams } from '../model/searchParams';
import trainerHoursFuncs, {
  TrainerHours,
  TrainerHoursFeeSums,
  TrainerHoursStatusEnum,
  Value,
} from '../model/trainerHours';
import ODataFilterBuilder from 'odata-filter-builder';
import { CONST } from '../utils/constants';
import { DefaultBackendHelper } from '../utils/backendHelper';
import { instance, URLS } from '.';
import { isEmptyId } from '../utils/generalUtils';

const logger = new Logger('backend.TrainerHours');
export interface BackendTrainerHours {
  getTrainerHours: (
    searchParams: SearchParams,
    trainerId?: string,
    minDate?: string,
    maxDate?: string,
    courseId?: string | null,
    costCenter?: string,
    departmentId?: string,
    locationId?: string
  ) => AxiosPromise<Value>;
  deleteTrainerHours(id: string): AxiosPromise;
  updateTrainerHours(trainerHours: TrainerHours): AxiosPromise<any>;
  addTrainerHoursByAttendances(attendanceIds: string[]): AxiosPromise<any>;
  exportTrainerHours: (
    trainerId?: string,
    year?: number,
    months?: number[],
    statuses?: TrainerHoursStatusEnum[],
    lang?: string
  ) => AxiosPromise;
  getTrainerHoursFeeSums(
    trainerId: string,
    year: number
  ): AxiosPromise<TrainerHoursFeeSums>;
}

export const defaultBackendTrainerHours: BackendTrainerHours = {
  getTrainerHours(
    searchParams: SearchParams,
    trainerId?: string,
    minDate?: string,
    maxDate?: string,
    courseId?: string | null,
    costCenter?: string,
    departmentId?: string,
    locationId?: string
  ): AxiosPromise<Value> {
    var odfb = ODataFilterBuilder('and');
    if (trainerId !== undefined) {
      odfb.eq(CONST.TrainerId, trainerId, false);
    }

    if (minDate !== undefined) {
      odfb.ge(CONST.Date, minDate, false);
    }
    if (maxDate !== undefined) {
      odfb.le(CONST.Date, maxDate, false);
    }

    if (courseId === null) {
      odfb.eq(CONST.CourseId, 'null', false);
    } else if (courseId !== undefined) {
      odfb.eq(CONST.CourseId, courseId, false);
    }

    if (costCenter !== undefined) {
      if (costCenter === '' || costCenter === null) {
        const odfbInner = ODataFilterBuilder('or');
        odfbInner.eq(CONST.CostCenter, 'null', false).eq(CONST.CostCenter, '');
        odfb.and(odfbInner);
      } else {
        odfb.eq(CONST.CostCenter, costCenter);
      }
    }

    let baseUrl;
    if (departmentId !== undefined) {
      baseUrl = `${URLS.trainerHoursOdata}/GetInDepartment(departmentId=${departmentId})`;
    } else if (locationId !== undefined) {
      baseUrl = `${URLS.trainerHoursOdata}/GetInLocation(locationId=${locationId})`;
    } else {
      baseUrl = URLS.trainerHoursOdata;
    }

    let url = DefaultBackendHelper.makeUrl(
      baseUrl,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter,
      odfb
    );
    logger.log(`getTrainerHours: ${url}`);
    return instance.get<Value>(url);
  },

  deleteTrainerHours(id: string): AxiosPromise {
    logger.debug('deleteTrainerHours');
    return instance.delete(`${URLS.trainerHours}/${id}`);
  },
  updateTrainerHours(trainerHours: TrainerHours): AxiosPromise<any> {
    logger.debug('updateTrainerHours');
    if (!isEmptyId(trainerHours.id)) {
      return instance.put<TrainerHours>(
        `${URLS.trainerHours}/${trainerHours.id}`,
        trainerHoursFuncs.toAPI(trainerHours)
      );
    } else {
      return instance.post<TrainerHours>(
        `${URLS.trainerHours}`,
        trainerHoursFuncs.toAPI(trainerHours)
      );
    }
  },
  addTrainerHoursByAttendances(attendanceIds: string[]): AxiosPromise<any> {
    return instance.post(`${URLS.trainerHours}/AddByAttendances`, {
      AttendanceIds: attendanceIds,
    });
  },
  exportTrainerHours(
    trainerId?: string,
    year?: number,
    months?: number[],
    statuses?: TrainerHoursStatusEnum[],
    lang?: string
  ): AxiosPromise {
    const url = `${URLS.trainerHours}/Export`;
    return instance.post(
      url,
      {
        TrainerId: trainerId,
        Year: year,
        Months: months,
        Statuses: statuses,
      },
      { responseType: 'blob', params: { lang: lang } }
    );
  },
  getTrainerHoursFeeSums(
    trainerId: string,
    year: number
  ): AxiosPromise<TrainerHoursFeeSums> {
    return instance.get<TrainerHoursFeeSums>(
      `${URLS.trainerHours}/TrainerHoursQueryFeeSums/${trainerId}/${year}`
    );
  },
};
